import type { WorkItem } from "../../experience-shell/workflows";

/**
 * Presentation-layer classification of a task work item along two
 * orthogonal axes — due-date lifecycle and the Ask/approval beat —
 * collapsed into a single rail-friendly enumeration.
 *
 * The due-date axis matches the server's authoritative bucketing in
 * <c>Galileo.Einstein.Domain.Workflows.WorkItemTally.Classify</c> so the
 * rail and the per-queue tally badges agree on the same vocabulary
 * (one ubiquitous language across the BFF boundary). Specifically:
 *
 *   - Completed: <c>completedAt</c> is set AND the asker's approval
 *     beat (if any) is satisfied.
 *   - Awaiting approval: completed AND a <c>requestedByQueueId</c>
 *     contract exists AND <c>approvedAt</c> is null. The asker still
 *     owes an approve/reject decision.
 *   - Open: not completed AND (no <c>dueDate</c> OR <c>dueDate</c> is
 *     past relative to <c>asOf</c>). Matches WorkItemTally's Open —
 *     "actionable now" absorbs the no-due-date case rather than
 *     splitting it into a fifth bucket.
 *   - Pending: not completed AND <c>dueDate</c> is in the future
 *     relative to <c>asOf</c>. Matches WorkItemTally's Pending —
 *     "parked for later."
 *
 * The <c>asOf</c> instant is a parameter rather than a hard-coded
 * <c>new Date()</c> so tests can pin the Open/Pending boundary
 * deterministically and callers can render against a freeze-frame
 * timeline if needed.
 */
export type TaskStatus = "open" | "pending" | "pending-approval" | "completed";

export function classifyTaskStatus(item: WorkItem, asOf: Date): TaskStatus {
    if (item.completedAt !== null) {
        if (item.requestedByQueueId != null && item.approvedAt == null) {
            return "pending-approval";
        }
        return "completed";
    }

    if (item.dueDate === null) return "open";

    const due = new Date(item.dueDate).getTime();
    if (Number.isNaN(due)) return "open";

    return due < asOf.getTime() ? "open" : "pending";
}

export function taskStatusLabel(status: TaskStatus): string {
    switch (status) {
        case "open":
            return "Open";
        case "pending":
            return "Pending";
        case "pending-approval":
            return "Awaiting approval";
        case "completed":
            return "Completed";
    }
}
